import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Repeat, CalendarDays, ArrowRight, Loader2, Star, X } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import StatusPill from "@/components/marketplace/StatusPill";
import { SR_STYLES } from "@/components/servicerequest/QuestionField";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

export const DateChips = ({ dates, onChange, disabled }) => {
  const [d, setD] = useState("");
  const add = () => { if (d && !dates.includes(d)) onChange([...dates, d].sort()); setD(""); };
  return (
    <div data-testid="manual-dates">
      <div className="flex gap-2">
        <input type="date" value={d} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setD(e.target.value)} disabled={disabled} className="sr-input max-w-[200px]" data-testid="manual-date-input" />
        <button type="button" onClick={add} disabled={!d || disabled} className="rounded-full border border-[#165a71]/40 text-[#165a71] px-4 text-sm disabled:opacity-50" data-testid="manual-date-add">Add date</button>
      </div>
      {dates.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {dates.map((x) => (
            <span key={x} className="inline-flex items-center gap-1 rounded-full bg-[#165a71]/10 text-[#165a71] px-3 py-1 text-xs" data-testid={`manual-date-${x}`}>
              {new Date(`${x}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              {!disabled && <button type="button" onClick={() => onChange(dates.filter((y) => y !== x))} data-testid={`manual-date-remove-${x}`}><X className="h-3 w-3" /></button>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

function NewPlanForm({ onCreated }) {
  const [f, setF] = useState({ title: "", price: "", checkout_time: "10:00", hours: 3, line1: "", city: "", state: "FL", zip_code: "", bedrooms: "", bathrooms: "", ical_url: "", notes: "" });
  const [dates, setDates] = useState([]);
  const [q, setQ] = useState("");
  const [cleaners, setCleaners] = useState([]);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => { const t = setTimeout(() => api.get("/turnovers/cleaners", { params: { q: q || undefined } }).then((r) => setCleaners(r.data.items)), 250); return () => clearTimeout(t); }, [q]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/turnovers/plans", {
        title: f.title, provider_id: pick.id, price_per_turnover: Number(f.price), checkout_time: f.checkout_time, hours: Number(f.hours) || 3,
        address_snapshot: { line1: f.line1, city: f.city, state: f.state, zip_code: f.zip_code }, bedrooms: f.bedrooms || null, bathrooms: f.bathrooms || null,
        ical_url: f.ical_url || null, manual_dates: dates, notes: f.notes || null,
      });
      toast.success(`Plan sent to ${pick.name} to accept`);
      onCreated(data);
    } catch (err) { toast.error(err?.response?.data?.error || "Could not create plan"); } finally { setBusy(false); }
  };
  const ok = f.title.trim() && Number(f.price) > 0 && f.city.trim() && pick && (f.ical_url.trim() || dates.length);

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 space-y-6" data-testid="turnover-new-form">
      <div className="font-display text-xl font-semibold text-[#071b22]">New turnover plan</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="sm:col-span-2"><div className="text-xs mb-1 text-[#252A34]/70">Rental name *</div><input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Seagrove beach house" className="sr-input" data-testid="tp-title" /></label>
        <input value={f.line1} onChange={(e) => set("line1", e.target.value)} placeholder="Street address" className="sr-input sm:col-span-2" data-testid="tp-line1" />
        <input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="City *" className="sr-input" data-testid="tp-city" />
        <div className="grid grid-cols-2 gap-3"><input value={f.state} onChange={(e) => set("state", e.target.value)} placeholder="State" className="sr-input" data-testid="tp-state" /><input value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} placeholder="ZIP" className="sr-input" data-testid="tp-zip" /></div>
        <input value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} placeholder="Bedrooms" className="sr-input" data-testid="tp-bedrooms" />
        <input value={f.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} placeholder="Bathrooms" className="sr-input" data-testid="tp-bathrooms" />
      </div>
      <div>
        <div className="text-sm font-semibold text-[#071b22]">Your booking calendar</div>
        <p className="text-xs text-[#252A34]/60 mt-0.5">In Airbnb: Calendar → Availability → Connect calendars → Export calendar, then copy the link. Vrbo works the same way. We check it every hour.</p>
        <input value={f.ical_url} onChange={(e) => set("ical_url", e.target.value)} placeholder="https://www.airbnb.com/calendar/ical/....ics" className="sr-input mt-2" data-testid="tp-ical" />
        <div className="text-xs text-[#252A34]/60 mt-3 mb-1">…and/or add checkout dates yourself</div>
        <DateChips dates={dates} onChange={setDates} />
      </div>
      <div className="grid gap-3 grid-cols-3">
        <label><div className="text-xs mb-1 text-[#252A34]/70">Price per turnover ($) *</div><input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="sr-input" data-testid="tp-price" /></label>
        <label><div className="text-xs mb-1 text-[#252A34]/70">Checkout time</div><input type="time" value={f.checkout_time} onChange={(e) => set("checkout_time", e.target.value)} className="sr-input" data-testid="tp-time" /></label>
        <label><div className="text-xs mb-1 text-[#252A34]/70">Hours per clean</div><input type="number" value={f.hours} onChange={(e) => set("hours", e.target.value)} className="sr-input" data-testid="tp-hours" /></label>
      </div>
      <div>
        <div className="text-sm font-semibold text-[#071b22] mb-2">Choose your cleaner *</div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name (turnover specialists shown first)" className="sr-input" data-testid="tp-cleaner-search" />
        <div className="mt-2 grid gap-2 sm:grid-cols-2 max-h-64 overflow-auto">
          {cleaners.map((c) => (
            <button type="button" key={c.id} onClick={() => setPick(c)} className={`rounded-2xl border p-3 text-left flex items-center gap-3 transition-colors ${pick?.id === c.id ? "border-[#165a71] bg-[#165a71]/5" : "border-slate-200 hover:border-[#165a71]/40"}`} data-testid={`tp-cleaner-${c.id}`}>
              <div className="h-9 w-9 rounded-full bg-[#165a71]/10 overflow-hidden shrink-0">{c.profile_image && <img src={c.profile_image} alt="" className="h-full w-full object-cover" />}</div>
              <div className="min-w-0"><div className="text-sm font-medium truncate">{c.name}</div><div className="text-[11px] text-[#252A34]/60 inline-flex items-center gap-1">{c.rating_avg ? <><Star className="h-3 w-3 fill-[#d9a441] text-[#d9a441]" />{Number(c.rating_avg).toFixed(1)}</> : "New"}</div></div>
            </button>
          ))}
          {!cleaners.length && <div className="text-sm text-[#252A34]/60">No cleaners found.</div>}
        </div>
      </div>
      <textarea rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Checklist & notes for every turnover (linens, restock, lockbox code given separately…)" className="sr-input" data-testid="tp-notes" />
      <button onClick={submit} disabled={!ok || busy} className="rounded-full bg-[#165a71] text-white px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="tp-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send plan to cleaner</button>
    </div>
  );
}

export default function TurnoversPage() {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  useDocumentTitle("Turnovers");
  useEffect(() => { api.get("/turnovers/plans").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  return (
    <DashboardShell>
      <style>{SR_STYLES}</style>
      <div className="max-w-5xl" data-testid="turnovers-page">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Vacation rentals</div>
            <h1 className="mt-1 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Turnover cleaning</h1>
            <p className="text-sm text-[#252A34]/65 mt-1">One cleaner, one price, booked automatically after every guest checkout.</p>
          </div>
          {!creating && <button onClick={() => setCreating(true)} className="rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium inline-flex items-center gap-2" data-testid="turnovers-new"><Plus className="h-4 w-4" /> New plan</button>}
        </div>
        {creating && <div className="mb-8"><NewPlanForm onCreated={(p) => navigate(`/dashboard/turnovers/${p.id}`)} /></div>}
        {items === null ? <div className="text-[#252A34]/60">Loading…</div> : items.length === 0 && !creating ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="turnovers-empty">
            <Repeat className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg font-semibold text-[#071b22]">No turnover plans yet.</div>
            <div className="text-sm text-[#252A34]/65 mt-1">Connect your Airbnb calendar and we'll book a clean after every checkout.</div>
          </div>
        ) : (
          <div className="grid gap-3">
            {items.map((p) => (
              <Link key={p.id} to={`/dashboard/turnovers/${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4 hover:border-[#165a71]/40" data-testid={`turnover-plan-${p.id}`}>
                <div className="h-12 w-12 rounded-xl bg-[#165a71]/10 text-[#165a71] flex items-center justify-center"><CalendarDays className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3"><span className="font-display font-semibold text-[#071b22] truncate">{p.title}</span><StatusPill status={p.status} /></div>
                  <div className="text-xs text-[#252A34]/60 mt-1">{p.role === "host" ? `Cleaner: ${p.provider_name}` : `Host: ${p.host_name || "—"}`} · ${p.price_per_turnover}/turnover · {p.next_turnover ? `next ${new Date(`${p.next_turnover}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "no upcoming turnovers"}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-[#165a71]" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
